<script setup lang="ts">
    import type { RuntimeConfig } from 'nuxt/schema';
    import { ref } from 'vue';

    const emits = defineEmits<{
        (e: 'success' | 'close'): void;
    }>();
    const { shopwareDown } = useShopwareDowntime();
    const { hfpPushInfo } = useNotificationHelpers();

    const { t } = useI18n();

    const loginErrors = ref<string[]>([]);

    const runtimeConfig: RuntimeConfig = useRuntimeConfig();

    const currentUrl = ref('');

    const formData = ref({
        username: '',
        password: '',
        remember: true
    });

    const goToRegister = ref();
    const { login } = useLoginState();
    const { getPostLoginUrl } = usePostLoginUrl();

    const invokeLogin = async (): Promise<void> => {
        if (shopwareDown.value) {
            hfpPushInfo(t('errors.shopwareLoginError'));
            return;
        }

        loginErrors.value = [];

        try {
            await login({ redirectTo: await getPostLoginUrl() });
        } catch (e) {
            console.error('[Checkout] login failed', e);
        }
    };

    onMounted(() => {
        currentUrl.value = encodeURIComponent(window.location.href);

        goToRegister.value = () => {
            emits('close');
            log.debug(
                `[AccountLoginForm.vue] Navigating to registration page: ${runtimeConfig.public.keycloakUrl}/register/?redirect_uri=${currentUrl.value}`
            );
            navigateTo(`${runtimeConfig.public.keycloakUrl}/register/?redirect_uri=${currentUrl.value}`, {
                external: true
            });
        };
    });
</script>

<template>
    <div
        id="modal-headline"
        role="form"
        title="Login form"
        aria-label="Login form"
        class="login-modal"
    >
        <h2 class="login-header">
            {{ t('account.signInLabel') }}
        </h2>

        <form @submit.prevent="invokeLogin">
            <input
                v-model="formData.remember"
                type="hidden"
                name="remember"
                data-testid="login-remember-input"
            />

            <slot :data="formData" />

            <slot name="error">
                <div
                    v-if="loginErrors.length"
                    data-testid="login-errors-container"
                >
                    {{ loginErrors }}
                </div>
            </slot>

            <div class="buttons">
                <PrimaryButton
                    product-button
                    class="full-width"
                    :button-text="t('account.signIn')"
                    :disabled="shopwareDown"
                    data-testid="login-submit-button"
                    type="submit"
                />

                <PrimaryButton
                    product-button
                    class="full-width"
                    :button-text="t('account.signUp')"
                    variant="secondary"
                    :disabled="shopwareDown"
                    data-testid="login-sign-up-link"
                    @click="goToRegister()"
                />
            </div>
        </form>

        <slot name="advantages" />
    </div>
</template>

<style scoped>
    @import '~/assets/scss/basics/login-modal.scss';
</style>
